import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../utils/firebase';
import { Study, CreateStudyFormData } from '../types/study.types';
import { retryFirebaseCall } from '../utils/retry';

interface APIResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface UseStudiesReturn {
  studies: Study[];
  loading: boolean;
  error: string | null;
  createStudy: (data: CreateStudyFormData) => Promise<string>;
  updateStudy: (studyId: string, updates: Partial<Study>) => Promise<void>;
  activateStudy: (studyId: string) => Promise<void>;
  getNextParticipantCode: (studyId: string) => Promise<string>;
  generateRCTCodes: (studyId: string, count: number) => Promise<string[]>;
  refreshStudies: () => void;
}

export const useStudies = (): UseStudiesReturn => {
  const [studies, setStudies] = useState<Study[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for studies
  useEffect(() => {
    const q = query(
      collection(db, 'studies'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const studiesData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as Study[];
        setStudies(studiesData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching studies:', err);
        setError(err.message || 'Failed to fetch studies');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  /**
   * Create a new study
   */
  const createStudy = useCallback(async (data: CreateStudyFormData): Promise<string> => {
    setError(null);

    try {
      const createStudyFn = httpsCallable<
        CreateStudyFormData,
        APIResponse<{ studyId: string }>
      >(functions, 'createStudy');

      const result = await retryFirebaseCall(
        () => createStudyFn(data),
        'createStudy'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to create study');
      }

      return result.data.data.studyId;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create study';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Update an existing study
   */
  const updateStudy = useCallback(async (studyId: string, updates: Partial<Study>): Promise<void> => {
    setError(null);

    try {
      const updateStudyFn = httpsCallable<
        { studyId: string; updates: Partial<Study> },
        APIResponse<void>
      >(functions, 'updateStudy');

      const result = await retryFirebaseCall(
        () => updateStudyFn({ studyId, updates }),
        'updateStudy'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to update study');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update study';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Activate a draft study
   */
  const activateStudy = useCallback(async (studyId: string): Promise<void> => {
    setError(null);

    try {
      const activateStudyFn = httpsCallable<
        { studyId: string },
        APIResponse<void>
      >(functions, 'activateStudy');

      const result = await retryFirebaseCall(
        () => activateStudyFn({ studyId }),
        'activateStudy'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to activate study');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to activate study';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Get next sequential participant code (for observational studies)
   */
  const getNextParticipantCode = useCallback(async (studyId: string): Promise<string> => {
    setError(null);

    try {
      const getCodeFn = httpsCallable<
        { studyId: string },
        APIResponse<{ code: string }>
      >(functions, 'getNextParticipantCode');

      const result = await retryFirebaseCall(
        () => getCodeFn({ studyId }),
        'getNextParticipantCode'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to generate code');
      }

      return result.data.data.code;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate participant code';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Generate batch of random codes (for RCT studies)
   */
  const generateRCTCodes = useCallback(async (studyId: string, count: number): Promise<string[]> => {
    setError(null);

    try {
      const generateCodesFn = httpsCallable<
        { studyId: string; count: number },
        APIResponse<{ codesGenerated: number; codes: string[] }>
      >(functions, 'generateRCTCodes');

      const result = await retryFirebaseCall(
        () => generateCodesFn({ studyId, count }),
        'generateRCTCodes'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to generate codes');
      }

      return result.data.data.codes;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate RCT codes';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Manual refresh (triggers re-render with current data)
   */
  const refreshStudies = useCallback(() => {
    // The real-time listener handles this automatically
    // This is here for API consistency
  }, []);

  return {
    studies,
    loading,
    error,
    createStudy,
    updateStudy,
    activateStudy,
    getNextParticipantCode,
    generateRCTCodes,
    refreshStudies,
  };
};
